import type { TherapeuticStage } from "~/database/types";
import {
    getPartTypeFamily,
    type PartTypeFamily,
} from "~/features/parts-similarity/utils/type-family";
import { UNBURDENED_STAGES } from "~/lib/unburdening-stages";

import type { MeditationCatalogId } from "../types";

export interface MeditationCatalogItem {
    id: MeditationCatalogId;
    title: string;
    description: string;
    byLine:
        | "Script by Jay Earley"
        | "Script by Michelle Glass"
        | "Written for this part"
        | "Written for this session";
    spokenMin: number;
    pauseMin: number;
    isDpmp: boolean;
    families: "all" | readonly PartTypeFamily[];
    stageRule: "any" | "hide-when-unburdened" | "only-when-unburdened";
    gate: "enable-dpmp" | null;
    /**
     * Fallback illustration for the picker card.
     *
     * AIDEV-NOTE: Resource-backed items normally draw their image from the resource itself
     * (api.meditation.setup-options), so their artwork stays in step with Resources without a
     * second place to update. This field exists for the generated check-in, which has no
     * resource — and as a backstop if a resource is ever seeded without an image. The asset was
     * drawn with the verbatim prompt from scripts/generate-resource-images.ts so it sits in the
     * same watercolor family as the rest.
     */
    imageUrl?: string;
    backing:
        | { kind: "generated" }
        // The full-session meditation: generated from the session report, not the part.
        // Only offered when the interaction came from a session (api.meditation.setup-options).
        | { kind: "session" }
        | {
              kind: "resource";
              collectionSlug:
                  | "self-therapy-exercises"
                  | "daily-parts-meditation";
              resourceSlug: string;
          };
}

// AIDEV-NOTE: The one meditation with no resource behind it, so the one that needs a static
// illustration. Drawn with the verbatim prompt from scripts/generate-resource-images.ts
// (soft watercolor, warm earth tones with cool blues and greens) so it reads as part of the
// same set as the resource artwork the other seven borrow. Regenerate with that prompt if
// it is ever replaced — do not swap in art from another source.
const PART_CHECK_IN_IMAGE_URL = "/meditation-art/part-check-in.webp";
// Same prompt and family as the check-in art; see the note above.
const FULL_SESSION_IMAGE_URL = "/meditation-art/full-session.webp";

// AIDEV-NOTE: Family availability (IFS consultant review, 2026-09-09): every script except the
// check-in treats its target as a PART to separate from, befriend, name or catalogue. Guides are
// not parts in this product (never framed as Self-like parts), so a guide gets the check-in only —
// turning toward a guide and asking what it has to offer is a legitimate solo practice, the rest
// would quietly reintroduce the ambiguity. Unattached burdens, "Self" entries and parts whose
// type is still unknown / combined get nothing: see isSoloMeditationEligible (Brent, 2026-09-09).
const PART_FAMILIES: readonly PartTypeFamily[] = ["protector", "exile"];
const PART_OR_GUIDE_FAMILIES: readonly PartTypeFamily[] = [
    ...PART_FAMILIES,
    "guide",
];

// AIDEV-NOTE: Stable slugs are the catalog contract; seeded experience UUIDs are intentionally not embedded here.
export const MEDITATION_CATALOG: readonly MeditationCatalogItem[] = [
    {
        id: "check_in",
        title: "Part check-in",
        backing: { kind: "generated" },
        families: PART_OR_GUIDE_FAMILIES,
        stageRule: "any",
        gate: null,
        spokenMin: 3.5,
        pauseMin: 1.5,
        // AIDEV-NOTE: This must describe what contactPartPrompt (utils/prompts.ts) actually
        // generates, not a generic check-in. All three of its branches share the same shape —
        // settle, Self energy, permission, connect through whichever channel is open, witness,
        // appreciate, close — and none of them ask the part "what it wants you to know". The
        // branch itself (distance vs. invitation) is chosen at generation time from an LLM
        // burden/trust read (derive-part-parameters.ts), so this names only the shared shape;
        // the exile wording lives in CONTEXTUAL_DESCRIPTIONS. Keep both in step with the prompt.
        description:
            "Settle, find some Self energy, then turn toward this part and stay with whatever it offers — a sensation, an image, or words.",
        byLine: "Written for this part",
        isDpmp: false,
        imageUrl: PART_CHECK_IN_IMAGE_URL,
    },
    {
        id: "unblend",
        title: "Unblending",
        backing: {
            kind: "resource",
            collectionSlug: "self-therapy-exercises",
            resourceSlug: "unblending-and-finding-self",
        },
        families: PART_FAMILIES,
        stageRule: "any",
        gate: null,
        spokenMin: 4,
        pauseMin: 2,
        description:
            "Find a little separation from this part, so you can meet it from Self rather than from inside it and see it more clearly.",
        byLine: "Script by Jay Earley",
        isDpmp: false,
    },
    {
        id: "access",
        title: "Finding this part",
        backing: {
            kind: "resource",
            collectionSlug: "self-therapy-exercises",
            resourceSlug: "accessing-a-part",
        },
        families: PART_FAMILIES,
        stageRule: "any",
        gate: null,
        spokenMin: 3.5,
        pauseMin: 2,
        description:
            "Reach this part through whichever door is open today — a feeling, a place in the body, an image, or an inner voice.",
        byLine: "Script by Jay Earley",
        isDpmp: false,
    },
    // AIDEV-NOTE: Title collision, resolved by BYLINE 2026-10-08 (Tamala Floyd / HWA, demo
    // prep). Two more of her Befriending Protector Parts practices now ship as part-picker
    // experiences (scripts/seed-hwa-protector-experiences-20261008.sql), seeded under HER
    // OWN section titles, minus the "Practice - " prefix:
    //   "Getting to Know a Protector"       (her question set, ~2x this one's length)
    //   "Building a Trusting Relationship"  (her capstone: unblend, clear concerned parts, interview)
    // The first of those differs from this entry's "Getting to know a protector" only in
    // case, so nothing was renamed to separate them: the BYLINE is the distinction — hers
    // are attributed to Tamala through the Healing Works Academy collection they live in,
    // this one's byLine reads "Script by Jay Earley" — and the two never share a list anyway
    // (hers are entitlement-gated resource experiences, not catalog entries; see below).
    // Leave THIS title alone; it is shipped.
    //
    // Why her two are NOT entries in this catalog, and must not be added without more work:
    //   1. HWA content is entitlement-gated (hwa_entitlement / features/hwa-access). Nothing on
    //      the resource backing path checks entitlement, so a catalog entry would hand paid
    //      course scripts to every user.
    //   2. `backing.resourceSlug` resolves ONE audio experience per resource
    //      (getPublishedExerciseByResourceSlug picks the first). Both of hers hang off
    //      hwa-befriending-protectors, so two entries would resolve to the same script.
    //   3. Stable slugs are this catalog's contract (see the note above MEDITATION_CATALOG);
    //      seeded experience UUIDs are deliberately not embedded here.
    // Protector-only filtering for hers therefore runs off the EXISTING resources-player
    // filter — config.partsFilter "protectors" + isProtector (features/resources/utils/
    // parts-filter.ts) — which is the same family rule `families: ["protector"]` applies
    // here via getPartTypeFamily. No second filter was introduced.
    {
        id: "protector_dialogue",
        title: "Getting to know a protector",
        backing: {
            kind: "resource",
            collectionSlug: "self-therapy-exercises",
            resourceSlug: "getting-to-know-a-protector",
        },
        families: ["protector"],
        stageRule: "any",
        gate: null,
        spokenMin: 4.5,
        pauseMin: 2.5,
        description:
            "Ask what its job is, what it fears would happen if it stopped doing it, and what it needs from you in return.",
        byLine: "Script by Jay Earley",
        isDpmp: false,
    },
    {
        id: "new_part",
        title: "New part or part in process",
        backing: {
            kind: "resource",
            collectionSlug: "daily-parts-meditation",
            resourceSlug: "new-part-or-part-in-process",
        },
        families: PART_FAMILIES,
        stageRule: "hide-when-unburdened",
        gate: "enable-dpmp",
        spokenMin: 4.5,
        pauseMin: 2.5,
        description:
            "A trust-building first visit with a part you have only just noticed, or one that has been witnessed but not yet healed.",
        byLine: "Script by Michelle Glass",
        isDpmp: true,
    },
    {
        id: "pcc",
        title: "Parts Catalogue Card",
        backing: {
            kind: "resource",
            collectionSlug: "daily-parts-meditation",
            resourceSlug: "parts-catalogue-card",
        },
        families: PART_FAMILIES,
        stageRule: "any",
        gate: "enable-dpmp",
        spokenMin: 4.5,
        pauseMin: 2.5,
        description:
            "Recall this part's story — where it came from, what it has carried, and what it brings you now. Ends with a card you fill in.",
        byLine: "Script by Michelle Glass",
        isDpmp: true,
    },
    {
        id: "naming",
        title: "Discovering a part's true name",
        backing: {
            kind: "resource",
            collectionSlug: "daily-parts-meditation",
            resourceSlug: "discovering-a-parts-true-name",
        },
        families: PART_FAMILIES,
        stageRule: "any",
        gate: "enable-dpmp",
        spokenMin: 3.5,
        pauseMin: 2,
        description:
            "Invite this part to share the name it would like to be called by. You can rename it in the app when you finish.",
        byLine: "Script by Michelle Glass",
        isDpmp: true,
    },
    {
        id: "dpum",
        title: "Post-unburdening check-in",
        backing: {
            kind: "resource",
            collectionSlug: "daily-parts-meditation",
            resourceSlug: "daily-post-unburdening-maintenance",
        },
        families: PART_FAMILIES,
        stageRule: "only-when-unburdened",
        gate: "enable-dpmp",
        spokenMin: 4,
        pauseMin: 2.5,
        description:
            "A daily visit for the weeks after unburdening: are the burdens still gone, and are the qualities you invited in still here?",
        byLine: "Script by Michelle Glass",
        isDpmp: true,
    },
    {
        // AIDEV-NOTE: Restored 2026-09-17 (Adam). This was the "Full Session Meditation" card
        // on the session Meditations tab until #1284 retired the tab; "Connect with this Part"
        // is now the only way into a meditation from session notes, so it lives in that
        // picker even though it is not about the selected part. The copy must say so up
        // front — the reader arrived by tapping one part's name. Keep it last in the list;
        // families mirror check_in so it appears wherever the strip does.
        id: "full_session",
        title: "Full session meditation",
        backing: { kind: "session" },
        families: PART_OR_GUIDE_FAMILIES,
        stageRule: "any",
        gate: null,
        // sessionMeditationPrompt asks for ~5 min spoken and ~7 min with pauses.
        spokenMin: 5,
        pauseMin: 2,
        description:
            "Not just this part: a longer meditation built from the whole session, visiting each part that came up and checking how it is doing.",
        byLine: "Written for this session",
        isDpmp: false,
        imageUrl: FULL_SESSION_IMAGE_URL,
    },
];

// AIDEV-NOTE: `part_interactions.type` doubles as the NOTE KIND on note-based rows
// (journal / biography / youtube — see api.parts.$partId.notes.ts and the meditation
// journal route). Those rows are not typed parts: they must never supply a part's type,
// stage or focal count, and never anchor a check-in's context. Journals saved from the
// player create exactly such rows, which is how a part briefly read as "journal".
export const NOTE_INTERACTION_TYPES: readonly string[] = [
    "journal",
    "biography",
    "youtube",
];

export function isNoteInteractionType(
    type: string | null | undefined,
): boolean {
    return NOTE_INTERACTION_TYPES.includes((type ?? "").trim().toLowerCase());
}

// AIDEV-NOTE: Not every part_interactions row is a part. Unattached burdens (confirmed or
// unconfirmed) have no one on the other end to check in with, unblend from or name — that is
// practitioner-session material — and the rare "Self" rows are not parts either. Parts whose
// type is still unknown, combined ("Exile/Protector") or "Other" are withheld too: without a
// type there is no safe way to pick an approach solo (Brent, 2026-09-09). For all of these the
// session-notes strip is hidden (PartMeditationButton) and the catalog is empty.
export function isSoloMeditationEligible(
    partType: string | null | undefined,
): boolean {
    const family = getPartTypeFamily(partType);
    if (family === "unattached-burden" || family === "unknown") return false;
    return (partType ?? "").trim().toLowerCase() !== "self";
}

export function isSelfLikePartType(
    partType: string | null | undefined,
): boolean {
    return (partType ?? "").toLowerCase().includes("self-like");
}

/**
 * The single description shown on a meditation card, already adapted to this part.
 *
 * AIDEV-NOTE: There is exactly ONE description element per card, in body copy (Adam,
 * 2026-09-09). The clinical caveats used to be a second, green callout below the
 * description; that made cards wildly different heights and left the shorter ones in a
 * grid row stretched with white space. They are now folded into the description itself,
 * which is why these overrides REPLACE `item.description` rather than appending to it.
 *
 * Keep every string here to roughly 21-25 words so cards in a row stay the same height.
 */
const CONTEXTUAL_DESCRIPTIONS: readonly {
    id: MeditationCatalogId;
    applies: (ctx: {
        family: PartTypeFamily;
        partType: string | null | undefined;
    }) => boolean;
    description: string;
}[] = [
    {
        // AIDEV-NOTE: Covers BOTH exile branches of contactPartPrompt. Which one runs is
        // decided at generation time by derivePartMeditationParameters — a heavily burdened
        // exile gets the distance protocol (acknowledged, never invited to connect), a lighter
        // one gets permission-then-invitation. The card promises only what both do.
        id: "check_in",
        applies: ({ family }) => family === "exile",
        description:
            "Settle, find some Self energy, then turn toward this tender part, checking with the parts that watch over it before any contact is invited.",
    },
    {
        // "Finding this part" deliberately activates a trailhead; for an exile that is an
        // invitation to flood, with no one there to negotiate with protectors.
        id: "access",
        applies: ({ family }) => family === "exile",
        description:
            "This one actively goes looking for the part, so if the parts that watch over it object, stop and choose the check-in instead.",
    },
    {
        id: "protector_dialogue",
        applies: ({ partType }) => isSelfLikePartType(partType),
        description:
            "Ask what its job is and what it fears would happen if it stopped. Self-like parts often answer as though they were you.",
    },
];

export function getMeditationDescription(
    option: { id: MeditationCatalogId; description: string },
    {
        family,
        partType,
    }: { family: PartTypeFamily; partType: string | null | undefined },
): string {
    const override = CONTEXTUAL_DESCRIPTIONS.find(
        (candidate) =>
            candidate.id === option.id &&
            candidate.applies({ family, partType }),
    );
    return override?.description ?? option.description;
}

export function isUnburdenedStage(
    therapeuticStage: TherapeuticStage | null | undefined,
): boolean {
    return therapeuticStage != null && UNBURDENED_STAGES.has(therapeuticStage);
}

export function isNewFocalPart({
    focusWeight,
    interactionCount,
    therapeuticStage,
}: {
    focusWeight: "substantial" | "present" | "brief" | null;
    interactionCount: number;
    therapeuticStage: TherapeuticStage | null;
}): boolean {
    // AIDEV-NOTE: Mirrors SessionRepository.getClientRecentNewFocalPart so the CTA and setup agree.
    return (
        focusWeight === "substantial" &&
        interactionCount === 1 &&
        !isUnburdenedStage(therapeuticStage)
    );
}

export function getAvailableMeditationCatalog({
    partType,
    therapeuticStage,
    dpmpEnabled,
    isNewFocal,
}: {
    partType: string | null | undefined;
    therapeuticStage: TherapeuticStage | null;
    dpmpEnabled: boolean;
    isNewFocal: boolean;
}): {
    family: PartTypeFamily;
    suggestedId: MeditationCatalogId;
    options: MeditationCatalogItem[];
} {
    const family = getPartTypeFamily(partType);
    const unburdened = isUnburdenedStage(therapeuticStage);
    if (!isSoloMeditationEligible(partType)) {
        return { family, suggestedId: "check_in", options: [] };
    }
    const options = MEDITATION_CATALOG.filter((item) => {
        if (item.families !== "all" && !item.families.includes(family)) {
            return false;
        }
        if (item.gate === "enable-dpmp" && !dpmpEnabled) return false;
        if (item.stageRule === "hide-when-unburdened" && unburdened) {
            return false;
        }
        if (item.stageRule === "only-when-unburdened" && !unburdened) {
            return false;
        }
        return true;
    });

    const availableIds = new Set(options.map((option) => option.id));
    const suggestedId =
        unburdened && availableIds.has("dpum")
            ? "dpum"
            : isNewFocal && availableIds.has("new_part")
              ? "new_part"
              : "check_in";

    return {
        family,
        suggestedId,
        options: [...options].sort((a, b) => {
            if (a.id === suggestedId) return -1;
            if (b.id === suggestedId) return 1;
            return 0;
        }),
    };
}

export function estimateMeditationMinutes(
    item: Pick<MeditationCatalogItem, "spokenMin" | "pauseMin">,
    multiplier: 1 | 2 | 3,
): number {
    return Math.round(item.spokenMin + item.pauseMin * multiplier);
}
